import React, { useCallback, useEffect, useState } from 'react';
import { retention } from '../services/api';

// CRM / Retenção — captar, converter e sobretudo reter. Lê snapshots pré-calculados (o motor de
// risco corre no backend/job). Cada sócio em risco mostra SEMPRE as razões (nunca só um número).
const LEVELS = [
  { key: 'critical', label: 'Crítico', color: 'var(--error)', bg: 'var(--error-bg)' },
  { key: 'high', label: 'Alto', color: 'var(--warning)', bg: 'var(--warning-bg)' },
  { key: 'medium', label: 'Médio', color: 'var(--warning-text)', bg: 'var(--warning-bg)' },
];
const SIGNALS = [
  ['', 'Todos os sinais'], ['sem treinar', 'Inatividade'], ['frequência', 'Frequência em queda'],
  ['no-shows', 'No-shows'], ['avença vence', 'Renovação'], ['avença em atraso', 'Dívida avença'],
  ['1ª visita', 'Nova adesão'],
];

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('pt-PT') : '—');

export default function RetentionDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [level, setLevel] = useState('');
  const [signal, setSignal] = useState('');
  const [list, setList] = useState(null);
  const [busy, setBusy] = useState(null);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try { const r = await retention.dashboard(); setData(r.data); setError(false); }
    catch (e) { setError(e?.response?.status === 403 ? 'disabled' : 'error'); }
    finally { setLoading(false); }
  }, []);

  const loadList = useCallback(async () => {
    try { const r = await retention.atRisk({ level: level || undefined, signal: signal || undefined }); setList(r.data); }
    catch { setList([]); }
  }, [level, signal]);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);
  useEffect(() => { if (level || signal) loadList(); else setList(null); }, [level, signal, loadList]);

  const resolve = async (userId) => {
    setBusy(userId);
    try { await retention.resolve(userId); await loadDashboard(); if (level || signal) await loadList(); }
    finally { setBusy(null); }
  };

  const recompute = async () => { setBusy('run'); try { await retention.run(); await loadDashboard(); } finally { setBusy(null); } };

  if (loading) return <div style={S.wrap}><p>A carregar retenção…</p></div>;
  if (error === 'disabled') return <div style={S.wrap}><div style={S.empty}>O módulo de Retenção está desligado neste ginásio.</div></div>;
  if (error || !data) return <div style={S.wrap}><div style={S.empty}>Não foi possível carregar a retenção.</div></div>;

  const t = data.top || {};
  const rows = list !== null ? list : (data.atRiskList || []);

  return (
    <div style={S.wrap}>
      <div style={S.header}>
        <h2 style={S.h2}>CRM · Retenção</h2>
        <button style={S.ghost} onClick={recompute} disabled={busy === 'run'}>{busy === 'run' ? 'A recalcular…' : 'Recalcular risco'}</button>
      </div>

      {/* Topo comercial + retenção */}
      <div style={S.grid}>
        <Stat label="Leads novos" value={t.newLeads ?? 0} />
        <Stat label="Por contactar" value={t.toContact ?? 0} />
        <Stat label="Taxa conversão" value={`${t.conversionRate ?? 0}%`} />
        <Stat label="Membros em risco" value={t.atRisk ?? 0} tone={(t.atRisk ?? 0) > 0 ? 'warn' : undefined} />
        <Stat label="Renovações próximas" value={t.upcomingRenewals ?? 0} />
        <Stat label="Recuperados" value={t.recovered ?? 0} tone={(t.recovered ?? 0) > 0 ? 'good' : undefined} />
      </div>

      {/* Filtros */}
      <div style={S.filters}>
        <span style={S.filterLabel}>Filtrar:</span>
        <button style={{ ...S.chip, ...(level === '' ? S.chipOn : {}) }} onClick={() => setLevel('')}>Todos</button>
        {LEVELS.map((l) => (
          <button key={l.key} onClick={() => setLevel(level === l.key ? '' : l.key)}
            style={{ ...S.chip, ...(level === l.key ? { background: l.color, color: '#fff', borderColor: l.color } : {}) }}>{l.label}</button>
        ))}
        <select value={signal} onChange={(e) => setSignal(e.target.value)} style={S.select}>
          {SIGNALS.map(([v, lbl]) => <option key={v} value={v}>{lbl}</option>)}
        </select>
      </div>

      {/* Lista prioritária */}
      <div style={S.tableWrap}>
        <table style={S.table} className="adm-table">
          <thead>
            <tr>
              <th style={S.th}>Membro</th><th style={S.th}>Risco</th><th style={S.th}>Motivos</th>
              <th style={S.th}>Última atividade</th><th style={S.th}>Dados</th><th style={S.th}></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} style={S.emptyCell}>Sem membros em risco com estes filtros. 👏</td></tr>}
            {rows.map((r) => {
              const lv = LEVELS.find((l) => l.key === r.level) || { label: r.level, color: 'var(--text-secondary)', bg: 'var(--bg-tertiary)' };
              return (
                <tr key={r.userId}>
                  <td style={S.td}><a href={`/admin/members/${r.userId}/360`} style={S.link}>{r.name || `#${r.userId}`}</a></td>
                  <td style={S.td} data-label="Risco"><span style={{ ...S.badge, background: lv.bg, color: lv.color }}>{lv.label} · {r.score}</span></td>
                  <td style={S.td} data-label="Motivos">
                    <div style={S.reasons}>{(r.reasons || []).map((x, i) => <span key={i} style={S.reason}>{x}</span>)}</div>
                  </td>
                  <td style={S.td} data-label="Última atividade">{fmtDate(r.lastActivityAt)}</td>
                  <td style={S.td} data-label="Dados"><Confidence value={r.confidence} /></td>
                  <td style={S.td} className="adm-actions"><button style={S.resolveBtn} disabled={busy === r.userId} onClick={() => resolve(r.userId)}>Resolver</button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, value, tone }) {
  const c = tone === 'warn' ? '#b45309' : tone === 'good' ? '#15803d' : '#111827';
  const border = tone === 'warn' ? '#fcd34d' : tone === 'good' ? '#bbf7d0' : '#e5e7eb';
  return (
    <div style={{ ...S.stat, borderColor: border }}>
      <div style={{ ...S.statValue, color: c }}>{value}</div>
      <div style={S.statLabel}>{label}</div>
    </div>
  );
}

// Confiança dos dados — evita falsos positivos: se não há controlo de acessos/reservas, dizemo-lo.
function Confidence({ value }) {
  const map = { full: ['Completa', '#15803d'], partial: ['Parcial', '#a16207'], limited: ['Limitada', '#9ca3af'] };
  const [lbl, color] = map[value] || ['—', '#9ca3af'];
  return <span title="Sinais realmente disponíveis neste ginásio" style={{ fontSize: 12, color }}>{lbl}</span>;
}

const S = {
  wrap: { maxWidth: 1200, margin: '0 auto', padding: 16 },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 },
  h2: { fontSize: 24, fontWeight: 800, margin: 0 },
  ghost: { padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border-color)', background: 'var(--bg-card)', cursor: 'pointer', fontSize: 13 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12, marginTop: 18 },
  stat: { border: '1px solid var(--border-color)', borderRadius: 12, padding: 16, background: 'var(--bg-card)' },
  statValue: { fontSize: 26, fontWeight: 800 },
  statLabel: { color: 'var(--text-secondary)', fontSize: 13, marginTop: 4 },
  filters: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 22 },
  filterLabel: { color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600 },
  chip: { padding: '6px 12px', borderRadius: 999, border: '1px solid var(--border-color)', background: 'var(--bg-card)', cursor: 'pointer', fontSize: 13 },
  chipOn: { background: 'var(--info)', color: '#fff', borderColor: 'var(--info)' },
  select: { padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border-color)', fontSize: 13, marginLeft: 'auto' },
  tableWrap: { marginTop: 16, overflowX: 'auto', border: '1px solid var(--border-color)', borderRadius: 12 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 14, minWidth: 760 },
  th: { textAlign: 'left', padding: '10px 12px', color: 'var(--text-secondary)', fontSize: 12, textTransform: 'uppercase', letterSpacing: '.04em', borderBottom: '1px solid var(--border-color)', background: 'var(--info-bg)' },
  td: { padding: '12px', borderBottom: '1px solid var(--border-color)', verticalAlign: 'top' },
  link: { color: 'var(--info-text)', fontWeight: 600, textDecoration: 'none' },
  badge: { display: 'inline-block', padding: '3px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' },
  reasons: { display: 'flex', flexWrap: 'wrap', gap: 4 },
  reason: { background: 'var(--bg-tertiary)', color: 'var(--info-text)', borderRadius: 6, padding: '2px 8px', fontSize: 12 },
  resolveBtn: { padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border-color)', background: 'var(--bg-card)', cursor: 'pointer', fontSize: 13 },
  empty: { color: 'var(--text-muted)', fontSize: 14, padding: 16 },
  emptyCell: { padding: 24, textAlign: 'center', color: 'var(--text-muted)' },
};
